"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.hrLeaveTransactionsQuerySchema = exports.hrLeaveCalendarQuerySchema = exports.hrLeaveRequestQuerySchema = exports.hrLeaveCancelSchema = exports.hrLeaveRejectSchema = exports.hrLeaveApproveSchema = exports.hrLeaveRequestCreateSchema = exports.hrLeavePolicyQuerySchema = exports.hrLeavePolicyUpdateSchema = exports.hrLeavePolicyCreateSchema = exports.hrLeaveTypeQuerySchema = exports.hrLeaveTypeUpdateSchema = exports.hrLeaveTypeCreateSchema = exports.hrLeaveTxnTypeEnum = exports.hrLeaveAppliesToEnum = exports.hrLeaveAccrualMethodEnum = exports.hrLeaveStatusEnum = exports.hrLeaveHalfDayEnum = void 0;
const zod_1 = require("zod");
/**
 * HR leave contracts — Phase 1B (docs/HR_PHASE1_ARCHITECTURE.md §7, §8).
 *
 * ONE authoritative leave domain: every leave request — staff or doctor —
 * is an `hr.LeaveRequest`. Balance is TRACKED (opening/accrued/adjusted/
 * used/reversed → closing), never a blocking gate in Phase 1 — no payroll/
 * accrual engine exists yet to make a balance number trustworthy enough to
 * refuse a request over.
 */
exports.hrLeaveHalfDayEnum = zod_1.z.enum(['FIRST_HALF', 'SECOND_HALF']);
exports.hrLeaveStatusEnum = zod_1.z.enum(['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED']);
exports.hrLeaveAccrualMethodEnum = zod_1.z.enum(['NONE', 'MONTHLY', 'YEARLY', 'ON_JOINING']);
exports.hrLeaveAppliesToEnum = zod_1.z.enum(['EMPLOYEE_TYPE', 'DESIGNATION', 'DEPARTMENT', 'ALL']);
exports.hrLeaveTxnTypeEnum = zod_1.z.enum(['OPENING', 'ACCRUAL', 'ADJUSTMENT', 'CONSUMPTION', 'REVERSAL', 'ENCASHMENT']);
// ── leave types (admin, hr:leave:config) ────────────────────────────────────
exports.hrLeaveTypeCreateSchema = zod_1.z.object({
    code: zod_1.z.string().trim().min(1).max(30),
    name: zod_1.z.string().trim().min(1).max(100),
    isPaid: zod_1.z.boolean().default(true),
    requiresDocument: zod_1.z.boolean().default(false),
    minDays: zod_1.z.coerce.number().min(0).max(365).default(0.5),
    maxConsecutiveDays: zod_1.z.coerce.number().int().positive().max(365).optional(),
    genderRestriction: zod_1.z.enum(['MALE', 'FEMALE']).optional(),
});
exports.hrLeaveTypeUpdateSchema = exports.hrLeaveTypeCreateSchema.partial().extend({ isActive: zod_1.z.boolean().optional() });
exports.hrLeaveTypeQuerySchema = zod_1.z.object({ includeInactive: zod_1.z.coerce.boolean().optional() });
// ── leave policies (admin, hr:leave:config) ─────────────────────────────────
exports.hrLeavePolicyCreateSchema = zod_1.z.object({
    leaveTypeId: zod_1.z.string().uuid(),
    appliesTo: exports.hrLeaveAppliesToEnum.default('ALL'),
    appliesToValue: zod_1.z.string().trim().max(100).optional(),
    accrualMethod: exports.hrLeaveAccrualMethodEnum.default('NONE'),
    accrualDays: zod_1.z.coerce.number().min(0).max(365).default(0),
    carryForwardMax: zod_1.z.coerce.number().min(0).max(365).optional(),
    encashable: zod_1.z.boolean().default(false),
    maxBalance: zod_1.z.coerce.number().min(0).max(365).optional(),
    effectiveFrom: zod_1.z.coerce.date(),
    effectiveTo: zod_1.z.coerce.date().optional(),
});
exports.hrLeavePolicyUpdateSchema = exports.hrLeavePolicyCreateSchema.partial().extend({ isActive: zod_1.z.boolean().optional() });
exports.hrLeavePolicyQuerySchema = zod_1.z.object({ leaveTypeId: zod_1.z.string().uuid().optional() });
// ── leave requests ───────────────────────────────────────────────────────────
exports.hrLeaveRequestCreateSchema = zod_1.z
    .object({
    leaveTypeId: zod_1.z.string().uuid(),
    fromDate: zod_1.z.coerce.date(),
    toDate: zod_1.z.coerce.date(),
    halfDay: exports.hrLeaveHalfDayEnum.optional(),
    reason: zod_1.z.string().trim().min(3).max(2000),
    documentRef: zod_1.z.string().trim().max(500).optional(),
})
    .refine((v) => v.toDate >= v.fromDate, { message: '`toDate` must not be before `fromDate`', path: ['toDate'] });
exports.hrLeaveApproveSchema = zod_1.z.object({ decisionReason: zod_1.z.string().trim().min(3).max(2000).optional() });
exports.hrLeaveRejectSchema = zod_1.z.object({ decisionReason: zod_1.z.string().trim().min(3).max(2000) });
exports.hrLeaveCancelSchema = zod_1.z.object({ cancelReason: zod_1.z.string().trim().min(3).max(2000) });
exports.hrLeaveRequestQuerySchema = zod_1.z.object({
    employeeId: zod_1.z.string().uuid().optional(),
    status: exports.hrLeaveStatusEnum.optional(),
    leaveTypeId: zod_1.z.string().uuid().optional(),
    from: zod_1.z.coerce.date().optional(),
    to: zod_1.z.coerce.date().optional(),
    cursor: zod_1.z.string().min(1).max(512).optional(),
    limit: zod_1.z.coerce.number().int().positive().max(200).default(50),
});
exports.hrLeaveCalendarQuerySchema = zod_1.z
    .object({
    from: zod_1.z.coerce.date(),
    to: zod_1.z.coerce.date(),
    departmentId: zod_1.z.string().uuid().optional(),
})
    .refine((v) => v.to >= v.from, { message: '`to` must not be before `from`', path: ['to'] })
    .refine((v) => (v.to.getTime() - v.from.getTime()) / 86_400_000 <= 186, { message: 'The calendar window may span at most 186 days', path: ['to'] });
exports.hrLeaveTransactionsQuerySchema = zod_1.z.object({
    leaveTypeId: zod_1.z.string().uuid().optional(),
    cursor: zod_1.z.string().min(1).max(512).optional(),
    limit: zod_1.z.coerce.number().int().positive().max(200).default(50),
});
//# sourceMappingURL=hr-leave.schema.js.map